
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { addDays, format, parse, isValid, subDays } from 'date-fns';
import { Calendar } from 'lucide-react';

const ConceptionCalculator = () => {
  const [startDate, setStartDate] = useState('');
  const [calculationType, setCalculationType] = useState('forward');
  const [cycleLength, setCycleLength] = useState('28');
  const [results, setResults] = useState<{
    conceptionDates?: string[];
    ovulationDate?: string;
    fertileWindowStart?: string;
    fertileWindowEnd?: string;
    message?: string;
  }>({});

  // Generate options for cycle length
  const cycleLengthOptions = Array.from({ length: 15 }, (_, i) => (21 + i).toString());

  const handleCalculate = () => {
    try {
      // Parse the start date
      const date = parse(startDate, 'yyyy-MM-dd', new Date());
      
      if (!isValid(date)) {
        setResults({ message: 'Please enter a valid date' });
        return;
      }
      
      // Convert cycle length to number
      const cycleLengthNum = parseInt(cycleLength, 10);
      
      // Assuming ovulation occurs ~14 days before next period (default luteal phase)
      // For a 28-day cycle, this is typically day 14
      const ovulationDay = cycleLengthNum - 14;
      
      let ovulationDate, fertileWindowStart, fertileWindowEnd;
      const conceptionDates = [];
      
      if (calculationType === 'forward') {
        // Calculate from the first day of last period
        ovulationDate = addDays(date, ovulationDay - 1); // -1 because day 1 is the date itself
        fertileWindowStart = addDays(ovulationDate, -5);
        fertileWindowEnd = ovulationDate;
        
        // The most likely conception dates are the 5 days leading up to and including ovulation
        for (let i = -5; i <= 0; i++) {
          conceptionDates.push(format(addDays(ovulationDate, i), 'MMMM d, yyyy'));
        }
      } else {
        // For backward calculation, assume the date is a due date
        // Due date is typically 280 days (40 weeks) from last period start
        const lastPeriod = subDays(date, 280);
        
        // Calculate ovulation based on last period
        ovulationDate = addDays(lastPeriod, ovulationDay - 1);
        fertileWindowStart = addDays(ovulationDate, -5);
        fertileWindowEnd = ovulationDate;
        
        // The most likely conception dates
        for (let i = -5; i <= 0; i++) {
          conceptionDates.push(format(addDays(ovulationDate, i), 'MMMM d, yyyy'));
        }
      }
      
      setResults({
        conceptionDates,
        ovulationDate: format(ovulationDate, 'MMMM d, yyyy'),
        fertileWindowStart: format(fertileWindowStart, 'MMMM d, yyyy'),
        fertileWindowEnd: format(fertileWindowEnd, 'MMMM d, yyyy')
      });
    } catch (error) {
      setResults({ message: 'Error calculating conception dates. Please check your inputs.' });
    }
  };

  return (
    <Card className="w-full">
      <CardContent className="pt-6">
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Conception Calculator</h3>
            <p className="text-sm text-muted-foreground">
              Calculate potential conception dates based on your cycle or due date.
            </p>
          </div>
          
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="calculationType">Calculation Method</Label>
              <Select 
                value={calculationType} 
                onValueChange={setCalculationType}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select calculation method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="forward">From First Day of Last Period</SelectItem>
                  <SelectItem value="backward">From Due Date</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="startDate">
                {calculationType === 'forward' ? 'First Day of Last Period' : 'Due Date'}
              </Label>
              <div className="flex">
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="flex-1"
                />
                <div className="ml-2 bg-muted w-10 flex items-center justify-center rounded-md">
                  <Calendar className="h-4 w-4" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {calculationType === 'forward' 
                  ? 'Enter the first day of your last menstrual period' 
                  : 'Enter your expected due date'}
              </p>
            </div>

            {calculationType === 'forward' && (
              <div className="grid gap-2">
                <Label htmlFor="cycleLength">Average Cycle Length</Label>
                <Select 
                  value={cycleLength} 
                  onValueChange={setCycleLength}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select cycle length" />
                  </SelectTrigger>
                  <SelectContent>
                    {cycleLengthOptions.map(option => (
                      <SelectItem key={option} value={option}>
                        {option} days
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  The average length of your menstrual cycle.
                </p>
              </div>
            )}
            
            <Button onClick={handleCalculate} className="mt-2">
              Calculate
            </Button>
          </div>

          {results.message ? (
            <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-md">
              {results.message}
            </div>
          ) : results.conceptionDates ? (
            <div className="mt-6 space-y-4">
              <div className="p-4 border rounded-md">
                <h4 className="text-lg font-medium mb-4">Conception Information</h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium">Most Likely Conception Dates:</p>
                    <div className="mt-2 space-y-1">
                      {results.conceptionDates.map((date, index) => (
                        <div 
                          key={index}
                          className={`p-2 rounded-sm ${index === results.conceptionDates!.length - 1 ? 'bg-primary/10 font-medium' : 'bg-muted'}`}
                        >
                          {date} {index === results.conceptionDates!.length - 1 ? '(Most Fertile)' : ''}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium">Estimated Ovulation Date:</p>
                    <p className="text-primary font-medium">{results.ovulationDate}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium">Fertile Window:</p>
                    <p>{results.fertileWindowStart} to {results.fertileWindowEnd}</p>
                  </div>
                </div>
              </div>
              
              <div className="text-sm text-muted-foreground">
                <p>Note: Conception typically occurs during your fertile window, which is the five days before ovulation plus the day of ovulation. The day of ovulation and the two days before it are usually the most fertile.</p>
              </div>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export default ConceptionCalculator;
